























































import {
	computed,
	defineComponent,
	nextTick,
	PropType,
	reactive,
	ref,
	toRefs,
} from '@vue/composition-api';
import { EventBusEvent, useEventBus } from '@front-app/services/EventBus';
import { unique } from '@front-app/util/array';
import { STATUS } from '@front-app/constants/state';
import { sleep } from '@front-app/util/async';
import { getCreateDiscussionPayload } from '@front-app/components/discussion/util';
import { useDiscussionStore } from '@front-app/store/piniaModules/discussions';
import { Group, Groups } from '@front-app/services/GroupsApi/GroupModel';
import {
	areDiscussionsVisibleForCurrentUser,
	canManageDiscussionCategories,
} from '@front-app/util/permission';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import LoadingView from './LoadingView.vue';
import DiscussionForm from './DiscussionForm.vue';
import { RequestState, MessageState } from './Model';

export default defineComponent({
	components: {
		DiscussionForm,
		LoadingView,
	},
	props: {
		groups: {
			type: Array as PropType<Groups>,
			default: () => [],
		},
		isOpen: Boolean,
		isMultiGroup: Boolean,
		disableAtMentions: Boolean,
		disableCategories: Boolean,
	},
	setup(props) {
		const { emit } = useEventBus();
		const state = reactive({
			requests: [] as RequestState[],
			discussableGroups: props.groups.filter((g: Group) => areDiscussionsVisibleForCurrentUser(g, CurrentUserStore.isCsr.value)),
			undiscussableGroups: props.groups.filter((g: Group) => !areDiscussionsVisibleForCurrentUser(g, CurrentUserStore.isCsr.value)),
		});

		// Categories
		const discussableGroupCategories = props.disableCategories
			? ref([])
			: computed(() => unique(state.discussableGroups.flatMap(({ categories }) => categories)));
		const canAddNewCategories = props.disableCategories
			? ref(false)
			: computed(() => state.discussableGroups.every((group) => canManageDiscussionCategories(group)));
		// End categories

		const createDiscussions = async (messageState: MessageState) => {
			if (state.discussableGroups === null || state.discussableGroups === undefined) {
				return;
			}
			state.requests = state.discussableGroups.map(({ id, name }) => {
				const request = {
					status: STATUS.LOADING,
					groupName: name,
					groupId: id,
					discussionId: '',
					promise: Promise.resolve(),
				};

				request.promise = useDiscussionStore().create(id, getCreateDiscussionPayload(messageState))
					.then((newId) => {
						request.discussionId = newId;
					})
					// This is used so the transition doesn't appear too fast
					.then(() => sleep(200))
					.then(() => {
						request.status = STATUS.SUCCESS;
					})
					.catch(() => {
						request.status = STATUS.ERROR;
					});
				return request;
			});

			Promise.all(state.requests.map(({ promise }) => promise))
				.then(() => {
					emit(EventBusEvent.DISCUSSION_CREATED);
				});

			await nextTick();

			const headerEl = document.querySelector('#createDiscussionsHeader') as null | HTMLInputElement;
			if (headerEl) {
				headerEl.focus();
			}
		};

		const hasCompletedLoading = computed(() => state.requests.length === state.discussableGroups.length
			&& state.requests.every(({ status }) => status !== STATUS.LOADING));

		const removeGroup = (groupId: string) => {
			state.discussableGroups = state.discussableGroups.filter(({ id }) => id !== groupId);
		};

		return {
			...toRefs(state),
			createDiscussions,
			hasCompletedLoading,
			removeGroup,
			// Categories
			discussableGroupCategories,
			canAddNewCategories,
		};
	},
});
